/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import { AelEvents, SystemVarsAccountingJobs } from '@gh/builder-shared-data';
import { ActionFlow, getAllNewSelectedEntities, getRouteInitData } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { DialogService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { GridDefinition, GridDefinitionFactory } from '@gh/core-ui-grid';
import { filter$, map$ } from '@gh/rx/operators';
import { assign } from 'lodash';
import {
  AccountingAccount,
  AccountingAccountFinderDialogConfig,
  getAccountingAccountId,
  SystemVarsAccountingBusinessService,
  SystemVarsAccountingJobsModel,
} from '../../../resources';
import { accountingAccountFinderDialogRoute } from '../accounting-account-dialog';
import { SystemVarsAccountingJobsFormState } from './system-vars-accounting-jobs-form.service';

@Component({
  selector: 'gh-system-vars-accounting-jobs-subtab',
  template: require('./system-vars-accounting-jobs-subtab.component.html'),
  viewProviders: [
    SystemVarsAccountingJobsFormState,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SystemVarsAccountingJobsSubtabComponent
  extends AbstractFormComponent<SystemVarsAccountingJobsModel>
  implements OnInit {

  gridDefinition: GridDefinition;
  originalJobs: SystemVarsAccountingJobsModel;
  jobType: SystemVarsAccountingJobs;
  eventTypeId: number;

  constructor(
    public form: SystemVarsAccountingJobsFormState,
    private route: ActivatedRoute,
    private translations: TranslationService,
    private gridDefinitionFactory: GridDefinitionFactory,
    private systemVarsBusiness: SystemVarsAccountingBusinessService,
    private userContext: BuilderUserContext,
    private navigationService: BuilderNavigationService,
    private pipelines: FormActionPipelines,
    private dialogService: DialogService,
    private actions: CommonActions,
  ) {
    super();
  }

  ngOnInit(): void {
    const { originalJobs, jobType } = getRouteInitData(this.route);

    assign(this, {
      originalJobs,
      jobType,
    });

    this.form.build(originalJobs);
    this.gridDefinition = this.buildGridDefinition();
    this.setEventTypeId(jobType);
  }

  get completeFlow(): ActionFlow<FormFlowState<SystemVarsAccountingJobsModel>> {
    return this.pipelines.complete({
      execute: this.actions.execute((state: FormFlowState<SystemVarsAccountingJobsModel>) =>
        this.systemVarsBusiness.updateAccountingJobs(this.jobType, state.form.submitValue)),
      finish: (state: FormFlowState<SystemVarsAccountingJobsModel>) => {
        const { value } = state;
        this.form.build(value);
      },
    });
  }

  onReset(): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.form.build(this.originalJobs);
      });
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      // tslint:disable-next-line:no-non-null-assertion
      entityId: entityId ? entityId : this.userContext.office!.id,
      entityType: entityType ? entityType : 'OFFICE',
      eventTypeIds: [this.eventTypeId],
      label: label ? label : 'lbl_menu_accounting',
    });
  }

  onDelete(record: AccountingAccount): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_account_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.deleteRecord(record));
  }

  onAdd(): void {
    this.dialogService.openWithInput(accountingAccountFinderDialogRoute, <AccountingAccountFinderDialogConfig>{
        multiselection: true,
        preselected: this.form.value.jobAccounts.map(getAccountingAccountId),
      })
      .afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((accounts: AccountingAccount[]) => this.form.addRecords(accounts));
  }

  private buildGridDefinition(): GridDefinition {
    return this.gridDefinitionFactory.buildManual([{
      field: 'code',
      headerName: this.translations.get('lbl_account'),
      width: 200,
      suppressSizeToFit: true,
    }, {
      field: 'name',
      headerName: this.translations.get('lbl_name'),
      width: 200,
    }]);
  }

  private setEventTypeId(jobType: SystemVarsAccountingJobs): void {
    switch (jobType) {
      case SystemVarsAccountingJobs.OVERHEADS:
        this.eventTypeId = AelEvents.Office.EditedAccountingOverhead;
        break;
      case SystemVarsAccountingJobs.ADVERTISE:
        this.eventTypeId = AelEvents.Office.EditedAccountingAdvertise;
        break;
      case SystemVarsAccountingJobs.WAGES:
        this.eventTypeId = AelEvents.Office.EditedAccountingWages;
        break;
      case SystemVarsAccountingJobs.LIABILITIES:
        this.eventTypeId = AelEvents.Office.EditedAccountingLiabilities;
        break;
      default:
        break;
    }
  }
}
